import type {
    SnackbarAction,
    SnackbarVariant,
} from 'components/SnackbarNotification/types';

/**
 * Represents a snackbar notification event that can be emitted through the event bus.
 * This type defines the structure of data required to display a snackbar notification.
 */
type SnackbarEvent = {
    id: string;
    message: string;
    variant?: SnackbarVariant;
    action?: SnackbarAction;
};

/**
 * Type definition for event bus listeners.
 * A listener is a callback function that gets invoked when an event is emitted.
 */
type EventBusListener<T> = (event: T) => void;

/**
 * EventBus class implements the publish-subscribe pattern and allows decoupled communication.
 * Components can subscribe to events without knowing who emits them,
 * and emitters can send events without knowing who listens to them.
 */
class EventBus<T> {
    /**
     * Set of registered listeners for this event bus instance.
     */
    private readonly listeners = new Set<EventBusListener<T>>();
    /**
     * Queue to hold events emitted before any listeners are registered.
     */
    private queue: T[] = [];

    /**
     * Emits an event to all registered listeners.
     * When an event is emitted, all subscribed listeners are invoked with the event data.
     */
    emit(event: T): void {
        this.queue.push(event);

        this.listeners.forEach((listener) => {
            try {
                listener(event);
            } catch (error) {
                console.error('EventBus listener error:', error);
            }
        });
    }

    /**
     * Subscribes a listener to the event bus.
     * The listener will be invoked whenever an event is emitted.
     */
    on(listener: EventBusListener<T>): () => void {
        this.listeners.add(listener);
        // flush queue
        this.queue.forEach((event) => listener(event));
        this.queue = [];

        return () => this.off(listener);
    }

    /**
     * Unsubscribes a specific listener from the event bus.
     * The listener will no longer receive events after this call.
     */
    private off(listener: EventBusListener<T>): void {
        this.listeners.delete(listener);
    }
}

/**
 * Singleton instance of EventBus specifically configured for snackbar notifications.
 * This global instance should be used throughout the application for all snackbar-related events.
 * Using a singleton ensures consistent event handling and prevents multiple event bus instances.
 */
const snackbarEventBus = new EventBus<SnackbarEvent>();

/**
 * Generates a unique ID for each snackbar event to ensure proper re-rendering
 * even when identical messages are emitted consecutively.
 */
const generateSnackbarId = (): string => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

export { generateSnackbarId, snackbarEventBus };
